import React, { useState } from "react";
import styles from "../Home/HomePage.module.css";
import { useNavigate } from "react-router-dom";

export default function MetersAddPage() {
  localStorage.setItem("navbar_state", "user");
  const navigate = useNavigate();
  
  // Use environment-appropriate API endpoint
  const API = "https://simplix.co.za/api";
  //const API = "http://127.0.0.1:8000/api";

  const [meterNumber, setMeterNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  // Securely retrieve owner email from authenticated session
  const ownerEmail = (
  localStorage.getItem("userEmail") ||
  JSON.parse(localStorage.getItem("profile") || "{}").email ||
  ""
).trim();

  // Secure authenticated fetch
  const authenticatedFetch = (url, options = {}) => {
    const token = localStorage.getItem("auth_token") || localStorage.getItem("token");
    
    if (!token) {
      window.location.href = "/#/login";
      throw new Error("Authentication required");
    }
    
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": `Bearer ${token}`,
      },
    });
  };

  // Input sanitization and validation
  const sanitizeAndValidate = (input) => {
    const sanitized = input.trim().replace(/[^0-9a-zA-Z]/g, '');
    
    if (!sanitized) {
      throw new Error("Enter a meter number.");
    }
    
    if (!/^[0-9a-zA-Z]{6,20}$/.test(sanitized)) {
      throw new Error("Invalid meter number format.");
    }
    
    return sanitized;
  };

  async function submit(e) {
    e.preventDefault();
    setError("");
    setMsg("");
    setLoading(true);

    try {
      // Authentication check
      if (!ownerEmail) {
        navigate("/#/login", { replace: true });
        return;
      }

      // Secure input validation
      const sanitizedMeterNumber = sanitizeAndValidate(meterNumber);

      // API call with authentication
      const r = await authenticatedFetch(`${API}/meters/add/`, {
        method: "POST",
        body: JSON.stringify({
          number: sanitizedMeterNumber,
          ownerEmail,
        }),
      });

      const j = await r.json().catch(() => ({}));

      if (!r.ok) {
        // Handle different error types securely
        const errorMessage = j?.error || "Failed to add meter.";
        throw new Error(errorMessage);
      }

      // Handle different success states
      const handleSuccess = (status, message) => {
        setMsg(message);
        
        if (status === "linked" || status === "already_linked_to_you") {
          setTimeout(() => {
            navigate("/meters", { replace: true });
          }, 1000);
        }
      };

      switch (j.status) {
        case "linked":
          handleSuccess("linked", "Meter linked to your profile.");
          break;
        case "already_linked_to_you":
          handleSuccess("already_linked_to_you", "This meter is already linked to your profile.");
          break;
        case "approval_requested":
          handleSuccess("approval_requested", 
            "This meter is currently linked to another user. An approval email has been sent. If they do not respond within 24 hours, the meter will automatically move to your profile.");
          break;
        default:
          setMsg("Operation completed successfully.");
      }

    } catch (e) {
      // Secure error handling - no information leakage
      setError(e.message || "Could not add meter. Please try again.");
      
      // Log security events for monitoring
      if (e.message.includes("Authentication required")) {
        log_security_event("AUTH_TOKEN_MISSING", { source: "meter_add_page" });
      }
      
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={`${styles.section} ${styles.meterAddSection}`}>
        <div className={styles.meterAddShell}>
          <div className={styles.meterAddIntro}>
            <p className={styles.sectionEyebrow}>Meters</p>
            <h1 className={styles.h2}>Add a meter</h1>
            <p className={styles.sectionIntro}>
              Enter your meter number. We'll verify that it exists and is active.
            </p>

            <div className={styles.meterAddInfoCard}>
              <span>Quick tip</span>
              <p>
                If the meter is already linked to another user, they'll receive
                an approval email. If they do not respond within 24 hours, the
                meter will automatically move to your profile.
              </p>
            </div>
          </div>

          <div className={styles.meterAddCard}>
            <form className={styles.authForm} onSubmit={submit}>
              <div className={styles.meterFormGroup}>
                <label htmlFor="meterNumber">Meter number</label>
                <input
                  id="meterNumber"
                  className={styles.authInput}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9a-zA-Z]{6,20}"
                  placeholder="Enter meter number"
                  value={meterNumber}
                  onChange={(e) => setMeterNumber(e.target.value)}
                  maxLength={20}
                  required
                  autoComplete="off"
                />
              </div>

              {error && <div className={styles.authError}>{error}</div>}
              {msg && <div className={styles.authSuccess}>{msg}</div>}

              <div className={styles.meterFormActions}>
                <button
                  type="button"
                  className={styles.inlineBtn}
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  className={styles.primaryBtn}
                  type="submit"
                  disabled={loading || !meterNumber.trim()}
                >
                  {loading ? "Checking…" : "Save meter"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

// Security event logging for monitoring
function log_security_event(event_type, data) {
  // In production, send to security monitoring service
  console.debug(`Security event: ${event_type}`, data);
}